import { Brain, ChatsCircle, Check, Cpu, Desktop, Lightning } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { IdeSession } from "~/api/opsTypes";
import {
  useAvailableThinkers,
  useCurrentWorkspace,
  useDispatches,
  useMyIdeWindows,
  useSwitchWorkspace,
} from "~/api/queries";
import { isConnectedTo } from "~/features/talk/sessionAreas";
import {
  CLAUDE_MODELS,
  EFFORTS,
  modelLabel,
  type Operation,
  operationFromDispatch,
  useVoice,
  type Voice,
} from "~/features/talk/voice";
import { Menu, type MenuItem } from "~/foundation/components/Menu";

/** "knowIt — MacBook" (the folder the window has open, then the machine). */
export function windowLabel(session: IdeSession): string {
  const folder = session.workspaceName ?? session.workspacePath?.split("/").pop() ?? "IDE";
  return session.machineName ? `${folder} — ${session.machineName}` : folder;
}

export function voiceFromSession(session: IdeSession, previous: Voice | null): Voice {
  return {
    kind: "ide",
    sessionKey: session.sessionKey,
    label: windowLabel(session),
    workspacePath: session.workspacePath,
    boundTeamId: session.boundTeamId,
    teamId: session.teamId ?? null,
    teamName: session.teamName ?? null,
    model: previous?.kind === "ide" ? previous.model : "claude-opus-5-5",
    effort: previous?.kind === "ide" ? previous.effort : null,
  };
}

/**
 * The header's model chooser. The chip ALWAYS names the model of the operation in progress —
 * a page's reported operation first, else the person's own live IDE dispatch — and otherwise
 * the chosen voice and model, which is what the next turn will use.
 */
export function ModelChooser() {
  const { t } = useTranslation();
  const { voice, setVoice, operations } = useVoice();
  // Every workspace is asked (a window registers under whichever workspace its extension is on), and
  // only the windows CONNECTED to this one are offered — signed in to it, or their folder bound to it
  // (owner, 2026-10-02: "do not show the ides that are not connected to the workspace").
  const { data: sessionsPage } = useMyIdeWindows();
  const { data: workspace } = useCurrentWorkspace();
  const switchWorkspace = useSwitchWorkspace();
  const { data: thinkers } = useAvailableThinkers();
  const { data: active } = useDispatches(true);

  const workspaceId = workspace?.id ?? null;
  const windows = useMemo(
    () =>
      (sessionsPage?.sessions ?? []).filter(
        (s) => s.isOwn && s.isOnline && !s.revokedAt && isConnectedTo(s, workspaceId),
      ),
    [sessionsPage, workspaceId],
  );
  const aiThinkers = useMemo(
    () =>
      (thinkers ?? [])
        .filter((th) => th.isAi && !th.isHuman && th.usable && th.kind !== "ide")
        .slice(0, 12),
    [thinkers],
  );

  // A voice pointing at a window that re-activated (new session key, same folder) follows it.
  useEffect(() => {
    if (voice?.kind !== "ide" || windows.length === 0) return;
    if (windows.some((w) => w.sessionKey === voice.sessionKey)) return;
    const same = windows.find((w) => w.workspacePath && w.workspacePath === voice.workspacePath);
    if (same) setVoice(voiceFromSession(same, voice));
  }, [voice, windows, setVoice]);

  const labels = useMemo(
    () => new Map(windows.map((w) => [w.sessionKey, windowLabel(w)] as const)),
    [windows],
  );
  const fallback = useMemo(
    () =>
      (active ?? [])
        .map((d) => operationFromDispatch(d, labels))
        .find((op): op is Operation => op !== null) ?? null,
    [active, labels],
  );
  const operation = operations[operations.length - 1] ?? fallback;

  const offline =
    voice?.kind === "ide" &&
    sessionsPage !== undefined &&
    !windows.some((w) => w.sessionKey === voice.sessionKey);

  const idleModel =
    voice?.kind === "ide" ? (modelLabel(voice.model) ?? t("window default")) : voice?.model;
  const text = operation
    ? `${operation.voiceLabel} · ${modelLabel(operation.model) ?? t("window default")} · ${operation.label}`
    : voice
      ? `${voice.kind === "ide" ? "IDE" : "AI"} ${voice.label}${idleModel ? ` · ${idleModel}` : ""}`
      : t("Choose who answers");
  const dot = operation
    ? operation.state === "waiting"
      ? "bg-warning animate-pulse"
      : "bg-success animate-pulse"
    : offline
      ? "bg-danger"
      : voice
        ? "bg-text-tertiary"
        : "border border-text-tertiary";

  const items: MenuItem[] = [
    { type: "heading", title: t("Who answers") },
    ...(windows.length === 0
      ? [
          {
            type: "button" as const,
            title: t("No IDE window of this workspace online — open VS Code with Docit"),
            icon: Desktop,
            disabled: true,
            onClick: () => undefined,
          },
        ]
      : windows.map<MenuItem>((w) => ({
          type: "button",
          title: `IDE · ${windowLabel(w)}${
            w.teamName && w.teamId !== workspace?.id ? ` · ${w.teamName}` : ""
          }`,
          icon: voice?.kind === "ide" && voice.sessionKey === w.sessionKey ? Check : Desktop,
          selected: voice?.kind === "ide" && voice.sessionKey === w.sessionKey,
          onClick: () => {
            setVoice(voiceFromSession(w, voice));
            // The window answers into ITS workspace, so the conversation happens there: follow it.
            if (w.teamId && workspace && w.teamId !== workspace.id) {
              switchWorkspace.mutate({
                workspaceId: w.teamId,
                returnTo: `${window.location.pathname}${window.location.search}`,
              });
            }
          },
        }))),
    ...aiThinkers.map<MenuItem>((th) => ({
      type: "button",
      title: `AI · ${th.name}${th.model ? ` (${th.model})` : ""}`,
      icon: voice?.kind === "ai" && voice.thinkerId === th.id ? Check : Cpu,
      selected: voice?.kind === "ai" && voice.thinkerId === th.id,
      onClick: () =>
        setVoice({ kind: "ai", thinkerId: th.id, label: th.name, model: th.model ?? null }),
    })),
  ];

  if (voice?.kind === "ide") {
    items.push(
      { type: "separator" },
      {
        type: "submenu",
        title: `${t("Model")}: ${modelLabel(voice.model) ?? t("window default")}`,
        icon: Brain,
        items: [
          ...CLAUDE_MODELS.map<MenuItem>((m) => ({
            type: "button",
            title: `${m.label} (${m.id})`,
            ...(voice.model === m.id ? { icon: Check, selected: true } : {}),
            onClick: () => setVoice({ ...voice, model: m.id }),
          })),
          {
            type: "button",
            title: t("Window default"),
            ...(voice.model === null ? { icon: Check, selected: true } : {}),
            onClick: () => setVoice({ ...voice, model: null }),
          },
        ],
      },
      {
        type: "submenu",
        title: `${t("Effort")}: ${voice.effort ?? t("default")}`,
        icon: Lightning,
        items: [
          ...EFFORTS.map<MenuItem>((e) => ({
            type: "button",
            title: e,
            ...(voice.effort === e ? { icon: Check, selected: true } : {}),
            onClick: () => setVoice({ ...voice, effort: e }),
          })),
          {
            type: "button",
            title: t("Default"),
            ...(voice.effort === null ? { icon: Check, selected: true } : {}),
            onClick: () => setVoice({ ...voice, effort: null }),
          },
        ],
      },
    );
  }
  items.push(
    { type: "separator" },
    { type: "route", title: t("Open conversations"), icon: ChatsCircle, to: "/talk" },
  );

  const tooltip = operation
    ? `${operation.voiceLabel} — ${operation.model ?? t("window default model")}${
        operation.effort ? ` · effort ${operation.effort}` : ""
      } — ${operation.label}${operation.state === "waiting" ? ` (${t("waiting for you in VS Code")})` : ""}`
    : offline
      ? t("The chosen IDE window is offline")
      : t("Who answers, and with which model");

  return (
    <Menu
      ariaLabel={t("Model chooser")}
      align="end"
      items={items}
      trigger={
        <button
          type="button"
          data-testid="model-chooser"
          title={tooltip}
          className="flex h-8 max-w-[360px] items-center gap-2 rounded-sm border border-input-border bg-bg px-2 text-small text-text hover:border-input-border-focus"
        >
          <span aria-hidden className={`size-2 shrink-0 rounded-full ${dot}`} />
          <span className="truncate" data-testid="model-chooser-text">
            {text}
          </span>
        </button>
      }
    />
  );
}
